import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { uploadImageToCloudinary } from '../lib/cloudinary';

const DEFAULT_HTML = '<p></p>';
const URL_PATTERN = /(https?:\/\/[^\s<]+)/gi;
const FONT_SIZE_OPTIONS = [
  { label: '小', value: '2' },
  { label: '標準', value: '3' },
  { label: '中', value: '4' },
  { label: '大', value: '5' },
  { label: '特大', value: '6' },
];
const HEADING_OPTIONS = [
  { label: '本文', value: 'P' },
  { label: '見出し2', value: 'H2' },
  { label: '見出し3', value: 'H3' },
];
const TOOL_HELP_ITEMS = [
  { icon: 'B', label: '太字' },
  { icon: 'I', label: '斜体' },
  { icon: 'U', label: '下線' },
  { icon: '見出し', label: '本文 / 見出し' },
  { icon: '•', label: '箇条書き' },
  { icon: '∞', label: 'リンク' },
  { icon: 'L', label: '左寄せ' },
  { icon: 'C', label: '中央寄せ' },
  { icon: 'R', label: '右寄せ' },
  { icon: '→', label: 'インデント' },
  { icon: '←', label: '戻す' },
  { icon: 'サイズ', label: '文字サイズ' },
  { icon: 'あ', label: '文字色' },
  { icon: 'あ', label: 'マーカー色' },
  { icon: '□', label: 'マーカー' },
  { icon: '▣', label: '画像追加' },
];
function escapeHtmlAttribute(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function getEditorImageLabel(figure) {
  const image = figure?.querySelector?.('img');
  if (!image) return '画像';
  const alt = String(image.getAttribute('alt') || '').trim();
  if (alt) return alt;
  const src = String(image.getAttribute('src') || '');
  try {
    const parsed = new URL(src);
    const filename = parsed.pathname.split('/').filter(Boolean).pop() || '';
    return decodeURIComponent(filename) || '画像';
  } catch {
    return '画像';
  }
}

const RichArticleEditor = forwardRef(function RichArticleEditor({ value, onChange, user }, ref) {
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const fileInputRef = useRef(null);
  const draggingFigureRef = useRef(null);
  const pendingImagesRef = useRef(new Map());
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [currentColor, setCurrentColor] = useState('#2f6f5d');
  const [currentHighlightColor, setCurrentHighlightColor] = useState('#fff59d');
  const [currentFontSize, setCurrentFontSize] = useState('3');
  const [currentHeading, setCurrentHeading] = useState('P');
  const [showHelp, setShowHelp] = useState(false);

  function serializeEditorNode(editor) {
    if (!editor) return DEFAULT_HTML;
    const clone = editor.cloneNode(true);

    clone.querySelectorAll('figure[data-editor-object]').forEach((figure) => {
      figure.removeAttribute('data-editor-object');
      figure.removeAttribute('draggable');
      figure.removeAttribute('contenteditable');
      figure.classList.remove('rich-editor-object');
      figure.classList.remove('rich-editor-object-dragging');
      figure.querySelectorAll('figcaption[data-editor-file-label]').forEach((caption) => caption.remove());
    });

    const nextHtml = clone.innerHTML === '<br>' ? DEFAULT_HTML : clone.innerHTML;
    return nextHtml || DEFAULT_HTML;
  }

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const nextValue = value || DEFAULT_HTML;
    if (serializeEditorNode(editor) !== nextValue) {
      editor.innerHTML = nextValue;
      hydrateMediaObjects();
    }
  }, [value]);

  useEffect(() => () => {
    pendingImagesRef.current.forEach((item) => {
      if (item.objectUrl) URL.revokeObjectURL(item.objectUrl);
    });
    pendingImagesRef.current.clear();
  }, []);

  function serializeEditorHtml() {
    const editor = editorRef.current;
    return serializeEditorNode(editor);
  }

  function syncValue() {
    cleanupRemovedPendingImages();
    const nextHtml = serializeEditorHtml();
    onChange?.(nextHtml);
  }

  function hydrateMediaObjects() {
    const editor = editorRef.current;
    if (!editor) return;
    editor.querySelectorAll('figure').forEach((figure) => {
      if (!figure.querySelector('img')) return;
      figure.setAttribute('data-editor-object', 'image');
      figure.setAttribute('draggable', 'true');
      figure.setAttribute('contenteditable', 'false');
      figure.classList.add('rich-editor-object');
      let label = figure.querySelector('figcaption[data-editor-file-label]');
      if (!label) {
        label = document.createElement('figcaption');
        label.setAttribute('data-editor-file-label', 'true');
        label.className = 'rich-editor-file-label';
        figure.append(label);
      }
      label.textContent = getEditorImageLabel(figure);
    });
  }

  function cleanupRemovedPendingImages() {
    const editor = editorRef.current;
    if (!editor) return;
    const activeIds = new Set(
      [...editor.querySelectorAll('img[data-pending-image-id]')].map((node) => node.getAttribute('data-pending-image-id')).filter(Boolean)
    );
    pendingImagesRef.current.forEach((item, id) => {
      if (activeIds.has(id)) return;
      if (item.objectUrl) URL.revokeObjectURL(item.objectUrl);
      pendingImagesRef.current.delete(id);
    });
  }

  function linkifyUrls() {
    const editor = editorRef.current;
    if (!editor) return false;

    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node = walker.nextNode();

    while (node) {
      const parentTag = node.parentElement?.tagName?.toUpperCase();
      if (node.nodeValue?.match(URL_PATTERN) && parentTag !== 'A') {
        textNodes.push(node);
      }
      node = walker.nextNode();
    }

    let updated = false;

    for (const textNode of textNodes) {
      const text = textNode.nodeValue || '';
      URL_PATTERN.lastIndex = 0;
      if (!URL_PATTERN.test(text)) continue;

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;

      text.replace(URL_PATTERN, (matched, _url, offset) => {
        if (offset > lastIndex) {
          fragment.append(document.createTextNode(text.slice(lastIndex, offset)));
        }

        const anchor = document.createElement('a');
        anchor.href = matched;
        anchor.textContent = matched;
        anchor.target = '_blank';
        anchor.rel = 'noreferrer';
        fragment.append(anchor);
        lastIndex = offset + matched.length;
        updated = true;
        return matched;
      });

      if (lastIndex < text.length) {
        fragment.append(document.createTextNode(text.slice(lastIndex)));
      }

      textNode.parentNode?.replaceChild(fragment, textNode);
    }

    return updated;
  }

  function syncValueWithLinkify() {
    linkifyUrls();
    hydrateMediaObjects();
    syncValue();
  }

  function saveSelection() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current?.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  }

  function restoreSelection() {
    const selection = window.getSelection();
    if (!selection) return;
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    selection.removeAllRanges();
    if (savedRangeRef.current) {
      selection.addRange(savedRangeRef.current);
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.addRange(range);
  }

  function exec(command, commandValue = null) {
    restoreSelection();
    document.execCommand(command, false, commandValue);
    saveSelection();
    syncValue();
  }

  function execWithCss(command, commandValue = null) {
    restoreSelection();
    document.execCommand('styleWithCSS', false, true);
    document.execCommand(command, false, commandValue);
    document.execCommand('styleWithCSS', false, false);
    saveSelection();
    syncValue();
  }

  function placeCaretInside(node) {
    const selection = window.getSelection();
    if (!selection || !node) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    savedRangeRef.current = range.cloneRange();
  }

  function insertHtml(html, caretMarkerId = '') {
    restoreSelection();
    document.execCommand('insertHTML', false, html);
    if (caretMarkerId && editorRef.current) {
      const marker = editorRef.current.querySelector(`[data-editor-caret="${caretMarkerId}"]`);
      if (marker) {
        marker.removeAttribute('data-editor-caret');
        if (!marker.innerHTML.trim()) {
          marker.innerHTML = '<br>';
        }
        placeCaretInside(marker);
      }
    }
    hydrateMediaObjects();
    saveSelection();
    syncValue();
  }

  function moveDraggedFigure(dropTarget, clientY) {
    const editor = editorRef.current;
    const draggingFigure = draggingFigureRef.current;
    if (!editor || !draggingFigure) return;

    const targetFigure = dropTarget?.closest?.('figure[data-editor-object]');
    if (targetFigure && targetFigure !== draggingFigure) {
      const rect = targetFigure.getBoundingClientRect();
      const shouldPlaceAfter = clientY > rect.top + rect.height / 2;
      targetFigure.parentNode?.insertBefore(draggingFigure, shouldPlaceAfter ? targetFigure.nextSibling : targetFigure);
    } else if (!targetFigure) {
      editor.append(draggingFigure);
      const lastElement = editor.lastElementChild;
      if (!lastElement || lastElement.tagName !== 'P') {
        const tail = document.createElement('p');
        tail.innerHTML = '<br>';
        editor.append(tail);
      }
    }

    draggingFigure.classList.remove('rich-editor-object-dragging');
    draggingFigureRef.current = null;
    hydrateMediaObjects();
    syncValue();
  }

  function handleInsertLink() {
    const url = window.prompt('リンクURLを入力してください', 'https://');
    if (!url) return;
    exec('createLink', url);
  }

  function handleFontSizeChange(nextValue) {
    setCurrentFontSize(nextValue);
    execWithCss('fontSize', nextValue);
  }

  function handleHeadingChange(nextValue) {
    setCurrentHeading(nextValue);
    exec('formatBlock', nextValue);
  }

  function handleHighlightColorChange(nextValue) {
    setCurrentHighlightColor(nextValue);
    execWithCss('hiliteColor', nextValue);
  }

  function renderToolButton({ label, icon, onClick, title = '' }) {
    return (
      <button
        type="button"
        className="rich-editor-tool"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        title={title || label}
        aria-label={label}
      >
        <span className="rich-editor-tool-icon" aria-hidden="true">{icon}</span>
        <span className="rich-editor-tool-label">{label}</span>
      </button>
    );
  }

  function createPendingImageMarkup(file) {
    const localId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const caretId = `caret-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const objectUrl = URL.createObjectURL(file);
    pendingImagesRef.current.set(localId, { file, objectUrl });
    const alt = escapeHtmlAttribute(file.name || '記事画像');

    return {
      caretId,
      html: `
      <figure>
        <img src="${objectUrl}" alt="${alt}" data-pending-image-id="${localId}" />
        <figcaption data-editor-file-label="true" class="rich-editor-file-label">${alt}</figcaption>
      </figure>
      <p data-editor-caret="${caretId}"><br></p>
    `,
    };
  }

  async function uploadFiles(fileList) {
    const files = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
    if (!files.length || uploading) return;

    setError('');
    try {
      for (const file of files) {
        const pendingImage = createPendingImageMarkup(file);
        insertHtml(pendingImage.html, pendingImage.caretId);
      }
    } catch (err) {
      setError(err?.message || '画像の追加に失敗しました。');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function persistPendingImages() {
    const editor = editorRef.current;
    if (!editor) return serializeEditorHtml();
    const pendingNodes = [...editor.querySelectorAll('img[data-pending-image-id]')];
    if (!pendingNodes.length) return serializeEditorHtml();

    setUploading(true);
    setError('');
    try {
      for (const node of pendingNodes) {
        const localId = node.getAttribute('data-pending-image-id');
        const pendingItem = localId ? pendingImagesRef.current.get(localId) : null;
        if (!localId || !pendingItem?.file) continue;

        const uploaded = await uploadImageToCloudinary(pendingItem.file, user?.uid);
        node.setAttribute('src', uploaded.secureUrl);
        node.setAttribute('data-cloudinary-public-id', uploaded.publicId || '');
        node.removeAttribute('data-pending-image-id');
        if (pendingItem.objectUrl) URL.revokeObjectURL(pendingItem.objectUrl);
        pendingImagesRef.current.delete(localId);
      }

      hydrateMediaObjects();
      const persistedHtml = serializeEditorHtml();
      onChange?.(persistedHtml);
      return persistedHtml;
    } catch (err) {
      const message = err?.message || '画像アップロードに失敗しました。';
      setError(message);
      throw err;
    } finally {
      setUploading(false);
    }
  }

  useImperativeHandle(ref, () => ({
    persistPendingImages,
  }));

  return (
    <div className="rich-editor-shell">
      <div className="rich-editor-toolbar-row">
        <div className="rich-editor-toolbar">
          <div className="rich-editor-toolbar-group">
            {renderToolButton({ label: '太字', icon: 'B', onClick: () => exec('bold') })}
            {renderToolButton({ label: '斜体', icon: 'I', onClick: () => exec('italic') })}
            {renderToolButton({ label: '下線', icon: 'U', onClick: () => exec('underline') })}
            <label className="rich-editor-select rich-editor-control" title="見出し">
              <span className="sr-only">見出し</span>
              <select
                value={currentHeading}
                onChange={(e) => handleHeadingChange(e.target.value)}
                aria-label="見出し"
              >
                {HEADING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            {renderToolButton({ label: '箇条書き', icon: '•', onClick: () => exec('insertUnorderedList') })}
            {renderToolButton({ label: 'リンク', icon: '∞', onClick: handleInsertLink })}
            {renderToolButton({ label: '左寄せ', icon: 'L', onClick: () => exec('justifyLeft') })}
            {renderToolButton({ label: '中央', icon: 'C', onClick: () => exec('justifyCenter') })}
            {renderToolButton({ label: '右寄せ', icon: 'R', onClick: () => exec('justifyRight') })}
            {renderToolButton({ label: '字下げ', icon: '→', onClick: () => exec('indent') })}
            {renderToolButton({ label: '戻す', icon: '←', onClick: () => exec('outdent') })}
            <label className="rich-editor-select rich-editor-control" title="文字サイズ">
              <span className="sr-only">文字サイズ</span>
              <select
                value={currentFontSize}
                onChange={(e) => handleFontSizeChange(e.target.value)}
                aria-label="文字サイズ"
              >
                {FONT_SIZE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label
              className="rich-editor-color rich-editor-control rich-editor-color-preview"
              title="文字色"
              style={{ '--swatch-color': currentColor }}
            >
              <span className="sr-only">文字色</span>
              <span className="rich-editor-color-icon rich-editor-color-icon-text" aria-hidden="true">あ</span>
              <input
                className="rich-editor-color-picker"
                type="color"
                value={currentColor}
                onChange={(e) => {
                  setCurrentColor(e.target.value);
                  execWithCss('foreColor', e.target.value);
                }}
                aria-label="文字色"
                onClick={(e) => e.stopPropagation()}
              />
            </label>
            <label
              className="rich-editor-color rich-editor-control rich-editor-color-preview"
              title="マーカー色"
              style={{ '--swatch-color': currentHighlightColor }}
            >
              <span className="sr-only">マーカー色</span>
              <span className="rich-editor-color-icon rich-editor-color-icon-highlight" aria-hidden="true">あ</span>
              <input
                className="rich-editor-color-picker"
                type="color"
                value={currentHighlightColor}
                onChange={(e) => handleHighlightColorChange(e.target.value)}
                aria-label="マーカー色"
                onClick={(e) => e.stopPropagation()}
              />
            </label>
            {renderToolButton({ label: 'マーカー', icon: '□', onClick: () => execWithCss('hiliteColor', currentHighlightColor) })}
            {renderToolButton({ label: '画像追加', icon: '▣', onClick: () => fileInputRef.current?.click() })}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            hidden
            onChange={(e) => uploadFiles(e.target.files)}
          />
        </div>
        <div className="rich-editor-help-toggle-wrap">
          {renderToolButton({ label: 'ヘルプ', icon: '!', onClick: () => setShowHelp((prev) => !prev), title: 'アイコンの意味を表示' })}
        </div>
      </div>
      {showHelp ? (
        <div className="rich-editor-help-strip">
          {TOOL_HELP_ITEMS.map((item) => (
            <div
              key={`${item.icon}-${item.label}`}
              className="rich-editor-help-chip"
              title={item.label}
              aria-label={item.label}
            >
              <span className="rich-editor-help-chip-icon" aria-hidden="true">{item.icon}</span>
              <strong>{item.label}</strong>
            </div>
          ))}
        </div>
      ) : null}

      <div className="rich-editor-grid">
        <div>
          <div className="rich-editor-stage">
            <div
              ref={editorRef}
              className={`rich-editor-surface${uploading ? ' rich-editor-surface-uploading' : ''}`}
              contentEditable={!uploading}
              suppressContentEditableWarning
              onInput={syncValueWithLinkify}
              onBlur={() => {
                saveSelection();
                syncValueWithLinkify();
              }}
              onKeyUp={(e) => {
                saveSelection();
                if (e.key === ' ' || e.key === 'Enter') {
                  syncValueWithLinkify();
                }
              }}
              onMouseUp={saveSelection}
              onDragStart={(e) => {
                const figure = e.target.closest?.('figure[data-editor-object]');
                if (!figure || uploading) return;
                draggingFigureRef.current = figure;
                figure.classList.add('rich-editor-object-dragging');
                if (e.dataTransfer) {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', 'rich-editor-image');
              }
              }}
              onDragEnd={() => {
                if (draggingFigureRef.current) {
                  draggingFigureRef.current.classList.remove('rich-editor-object-dragging');
                }
                draggingFigureRef.current = null;
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (e.dataTransfer) {
                  e.dataTransfer.dropEffect = uploading ? 'none' : (draggingFigureRef.current ? 'move' : 'copy');
                }
              }}
              onPaste={() => {
                window.setTimeout(() => {
                  saveSelection();
                  syncValueWithLinkify();
                }, 0);
              }}
              onDrop={async (e) => {
                e.preventDefault();
                if (uploading) return;
                if (draggingFigureRef.current) {
                  moveDraggedFigure(e.target, e.clientY);
                  return;
                }
                saveSelection();
                await uploadFiles(e.dataTransfer.files);
              }}
            />
            {uploading ? (
              <div className="rich-editor-overlay" aria-live="polite">
                <strong>保存準備中...</strong>
                <span>本文画像を Cloudinary に保存しています。完了までお待ちください。</span>
              </div>
            ) : null}
          </div>
          <p className="hint">画像はここへドラッグ&ドロップできます。ここでは本文に仮表示され、下書き保存または投稿申請のときに Cloudinary へ保存されます。</p>
          {error ? <p className="error-text">{error}</p> : null}
        </div>
      </div>
    </div>
  );
});

export default RichArticleEditor;
